<template>
    <div id="notifications-page">
        <NotificationSidebar :filter="filter" @filtered="updateFilters" />

        <div class="page-content" ref="container">
            <header>{{ $t('notification.notifications') }}</header>
            <section class="toolbar">
                <div class="btnGroup">
                    <SupsisButton :classNames="'btnGroupItem'" @click="includeAll = false" :primary="!includeAll">
                        {{ $t('message.unreadTitle') }}
                    </SupsisButton>
                    <SupsisButton :classNames="'btnGroupItem'" @click="includeAll = true" :primary="includeAll">
                        {{ $t('message.all') }}
                    </SupsisButton>
                </div>
                <SupsisButton @click="markAsSeenAll">
                    <i class="material-icons">done</i>
                    {{ $t('mail.markAsAllRead') }}
                </SupsisButton>
            </section>
            <div class="page-content-wrapper">
                <NotificationListView
                    :notifications="notifications"
                    :loading="loading"
                    :group-by-date="true"
                    @load-more="loadMore"
                />
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import { mapActions } from "vuex";
import _ from "lodash";
import { ACTIVITY_API } from "../../../api/activity";
import NotificationListView from "./NotificationListView.vue";
import NotificationSidebar from "./NotificationSidebar.vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import { filter } from "rxjs/operators";
export default Vue.extend({
    name: "NotificationsPage",
    components: { SupsisButton, NotificationSidebar, NotificationListView },
    data() {
        return {
            includeAll: true,
            filter: {},
            loading: false,
            notifications: [],
            total: [],
            cursor: {
                page: 1,
                perPage: 50,
            },
            subscribes: [],
        };
    },
    methods: {
        ...mapActions({
            setNotificationCount: "app/setNotificationCount",
        }),
        updateFilters(filter) {
            this.filter = filter;
        },
        fetch(pagination) {
            this.loading = true;
            const fn = this.includeAll
                ? ACTIVITY_API.NOTIFICATION.getAllNotifications
                : ACTIVITY_API.NOTIFICATION.getUnreadNotifications;
            this.loading = true;
            fn(this.cursor, this.filter)
                .then((res) => {
                    const { count, items } = res.data;
                    this.notifications = _.uniqBy(pagination ? [...this.notifications, ...items] : items, (q) => q._id);
                    this.total = count;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        goBack() {
            this.$router.back();
        },
        markAsSeenAll() {
            ACTIVITY_API.NOTIFICATION.markAsSeenAll().then(() => {
                this.notifications = this.notifications.map((item) => {
                    item.seen = true;
                    return item;
                });
                this.setNotificationCount(0);
            });
        },
        loadMore() {
            if (Math.ceil(this.total / this.cursor.perPage) > this.cursor.page) {
                this.cursor.page = this.cursor.page + 1;
            }
        },
    },
    mounted() {
        this.fetch();
        const notificationUpdated = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.NOTIFICATION_UPDATED))
            .subscribe((x) => {
                const { id, seen } = x.payload;
                this.notifications = this.notifications.map((item) => {
                    if (item._id === id) item.seen = seen;
                    return item;
                });
            });

        this.subscribes.push(() => {
            notificationUpdated.unsubscribe();
        });
    },
    watch: {
        includeAll: {
            deep: true,
            handler() {
                this.cursor.page = 1;
                this.fetch();
            },
        },
        cursor: {
            deep: true,
            handler() {
                this.fetch(true);
            },
        },
        filter: {
            deep: true,
            handler() {
                this.fetch();
            },
        },
    },
    beforeDestroy() {
        this.subscribes.forEach((fn) => fn());
    },
});
</script>

<style scoped lang="scss">
#notifications-page {
    display: flex;
    width: 100%;
    height: 100%;
    position: relative;
    background-color: var(--color-bg-default);
    font-size: 14px;
    font-weight: 400;
    color: var(--color-text-default);
    line-height: 24px;

    header {
        max-width: 1000px;
        width: 100%;
        margin: 0 auto;
        font-weight: 700;
        color: var(--color-text-strong-default);
        font-size: 18px;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        max-width: 1000px;
        width: 100%;
        margin: 0 auto;
        padding-bottom: 20px;
        border-bottom: 1px solid var(--color-border-default);

        .btnGroup {
            display: flex;
            border: 1px solid var(--color-border-default);
            border-radius: 6px;
            background: var(--color-bg-neutral-default);
            padding: 2px;

            .btnGrupItem {
                &.selected {
                    background: var(--color-bg-oncomponent-default);
                    box-shadow: var(--shadow-interaction-200);
                }
            }

            .supsis-button {
                border: none !important;
                font-size: 14px;
            }
        }
    }

    .page-content {
        display: flex;
        flex-direction: column;
        gap: 10px;
        justify-content: flex-start;
        width: 100%;
        position: relative;
        padding: 20px;
        overflow-y: auto;
        height: calc(100vh - 93px);
        max-height: 100%;

        .page-content-wrapper {
            max-width: 1000px;
            width: 100%;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 10px;
            height: calc(100% - 106px);
            overflow: auto;
            .group {
                border: 1px solid var(--color-border-default);
                border-radius: 4px;

                .date {
                    position: relative;
                    display: inline;

                    &:after {
                        position: absolute;
                        left: 50%;
                        bottom: -15px;
                        height: 12px;
                        width: 1px;
                        content: "";
                        background: var(--color-border-default);
                    }
                }

                .items {
                    display: flex;
                    flex-direction: column;
                    border: 1px solid var(--color-border-default);
                    border-radius: 4px;
                    margin-top: 11px;

                    .activity-card:last-child {
                        border-bottom: none;
                    }

                    .activity-card {
                        display: flex;
                        justify-content: space-between;
                        border-bottom: 1px solid var(--color-border-default);
                        background-color: var(--color-bg-default);
                        padding: 20px;
                        border-radius: 4px 4px 0 0;
                        cursor: pointer;

                        &.note {
                            .left {
                                .content {
                                    display: unset !important;
                                }
                            }
                        }

                        &.rating {
                            .left {
                                .content {
                                    gap: 3px;
                                    display: flex;
                                    align-items: center;
                                    flex-wrap: wrap;

                                    #rating-yes,
                                    #rating-strong-yes {
                                        width: 20px;
                                        height: 20px;
                                        fill: #1ab616;
                                    }

                                    #rating-not-sure {
                                        fill: #f09a0f;
                                        width: 20px;
                                        height: 20px;
                                    }

                                    #rating-no {
                                        fill: #e50101;
                                        width: 20px;
                                        height: 20px;
                                    }
                                }
                            }
                        }

                        .quote {
                            padding-left: 8px;
                            border-left: 2px solid var(--color-border-default);
                            font-style: italic;
                            line-height: 24px;
                            display: block;
                        }

                        .tag {
                            background-image: var(--color-bg-default);
                            border: 1px solid var(--color-border-default);
                            line-height: 20px;
                            padding: 0 8px;
                            border-radius: 4px;
                            cursor: inherit;
                            display: inline-block;
                            color: var(--color-text-default);
                            font-size: 14px;
                            font-weight: 600;
                            position: relative;
                            margin-left: 5px;
                        }

                        .left {
                            display: flex;
                            gap: 10px;
                            align-items: center;

                            .avatar-wrapper {
                                display: flex;
                                flex-direction: row;
                                align-items: center;
                                position: relative;

                                .icon {
                                    display: flex;
                                    width: 24px;
                                    height: 24px;
                                    border-radius: 50%;
                                    position: absolute;
                                    align-content: center;
                                    align-items: center;
                                    justify-content: center;

                                    right: -8px;
                                    bottom: -8px;
                                    background-color: var(--color-bg-default);
                                    color: var(--color-icon-default);

                                    .MuiSvgIcon-root {
                                        color: var(--color-icon-default);
                                        width: 18px;
                                        height: 18px;
                                    }

                                    svg.text-danger {
                                        fill: var(--color-bg-critical-default) !important;
                                    }

                                    svg.text-success {
                                        fill: var(--color-bg-success-default) !important;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
